import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

gsap.registerPlugin(ScrollTrigger);

const FRAMEWORK_POINTS = [
  {
    title: "SSC Board + CBSE-aligned pattern",
    text: "We blend the structural benefits of the SSC Board with a nationally benchmarked CBSE-aligned pattern for Grades 1 through 8.",
  },
  {
    title: "Kreedo Experiential Lab",
    text: 'Integrates a hands-on, activity-based methodology ("Concrete to Abstract") designed to build early cognitive, spatial, and analytical skills.',
  },
  {
    title: "Abacus Mental Arithmetic",
    text: "Boosts brain agility, concentration, memory retention, and mathematical calculation speed from a young age.",
  },
];

const COURSES = [
  { classes: "Nursery & LKG", subjects: ["English", "Maths", "GK"] },
  { classes: "UKG", subjects: ["English", "Maths", "Marathi", "GK"] },
  {
    classes: "Std I, II & III",
    subjects: ["English", "Maths", "EVS", "Marathi", "Drawing Craft", "GK", "Computer"],
  },
  {
    classes: "Std IV, V, VI, VII & VIII",
    subjects: ["English", "Maths", "Science", "Marathi", "SST", "Hindi", "Drawing Craft", "Computer"],
  },
  {
    classes: "Std IX & X",
    note: "SSC board pattern and books",
    subjects: ["English", "Algebra", "Geometry", "Biology", "Chemistry", "Physics", "History", "Geography"],
  },
];

const MARQUEE_ITEMS = [
  "Foundational Excellence",
  "Hybrid Academic Framework",
  "Kreedo Experiential Lab",
  "Abacus Mental Arithmetic",
  "Course Studies",
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-12%" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const FramedImage = ({ src, alt, testId }) => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [28, -28]);

  return (
    <motion.div
      ref={ref}
      style={{ y: reduce ? 0 : y }}
      className="relative mx-auto w-full max-w-sm"
    >
      <div className="relative aspect-[2/3] overflow-hidden rounded-2xl shadow-[0_36px_70px_-28px_rgba(10,38,71,0.45)] ring-1 ring-[#143A66]/15">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#0E2C4E] px-6 text-center">
          <span className="block h-16 w-16 overflow-hidden rounded-full opacity-50 ring-1 ring-white/20">
            <img
              src="/logo.png"
              alt=""
              className="h-full w-full scale-[2.9] object-cover object-[49%_49%]"
            />
          </span>
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-[#D6EBEE]/50">
            [ Photograph placeholder ]
            <br />
            <span className="normal-case tracking-normal">{src}</span>
          </p>
        </div>
        {!failed && (
          <img
            src={src}
            alt={alt}
            data-testid={testId}
            loading="lazy"
            onError={() => setFailed(true)}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </div>
    </motion.div>
  );
};

export default function Academics() {
  const bgRef = useRef(null);
  const heroRef = useRef(null);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        bgRef.current,
        { backgroundColor: "#0A2647" },
        {
          backgroundColor: "#F2F8F9",
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "bottom 95%",
            end: "bottom 25%",
            scrub: true,
          },
        }
      );
    });
    return () => ctx.revert();
  }, []);

  const goContact = () => {
    navigate("/");
    setTimeout(() => {
      if (window.__lenis) window.__lenis.scrollTo("#contact", { duration: 1.6 });
      else
        document
          .getElementById("contact")
          ?.scrollIntoView({ behavior: "smooth" });
    }, 700);
  };

  return (
    <main className="relative">
      <div
        ref={bgRef}
        className="fixed inset-0 -z-10"
        style={{ backgroundColor: "#0A2647" }}
        aria-hidden="true"
      />
      <Navbar />

      <section
        ref={heroRef}
        className="relative flex min-h-[92svh] flex-col items-center justify-center px-6 text-center text-white"
        data-testid="academics-hero"
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-[11px] font-semibold uppercase tracking-[0.45em] text-[#D6EBEE]/80"
        >
          Val-Hue High School &amp; Junior College
        </motion.p>
        <h1 className="mt-6 font-display text-6xl font-semibold tracking-[0.02em] sm:text-7xl md:text-8xl">
          <span className="block overflow-hidden pb-3">
            <motion.span
              initial={{ y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="block"
              data-testid="academics-title"
            >
              Academics
            </motion.span>
          </span>
        </h1>
        <motion.span
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 block h-[2px] w-24 origin-center bg-[#F47221]"
          aria-hidden="true"
        />
      </section>

      <div
        className="overflow-hidden border-y border-[#143A66]/10 py-5"
        aria-hidden="true"
      >
        <div className="marquee-track items-center gap-10 text-[11px] font-semibold uppercase tracking-[0.35em] text-[#143A66]/55">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center gap-10 pr-10">
              {MARQUEE_ITEMS.map((item) => (
                <span key={item} className="flex items-center gap-10 whitespace-nowrap">
                  {item}
                  <span className="text-[#F47221]">•</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <section className="relative" data-testid="academics-framework-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-24 md:py-32 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <Reveal>
            <FramedImage
              src="/academics-foundational.jpg"
              alt="Foundational learning at Val-Hue"
              testId="foundational-image"
            />
          </Reveal>
          <div>
            <Reveal>
              <p className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#F47221]">
                <span className="font-display text-2xl tracking-normal text-[#143A66]/25">
                  01
                </span>
                Framework
              </p>
              <h2 className="mt-5 font-display text-3xl font-semibold leading-snug text-[#143A66] md:text-4xl">
                Foundational Excellence &amp; Hybrid Academic Framework
              </h2>
            </Reveal>
            <div className="mt-10 space-y-8">
              {FRAMEWORK_POINTS.map((p, i) => (
                <Reveal key={p.title} delay={0.08 * i} className="flex gap-5">
                  <span
                    className="mt-2 h-[2px] w-8 shrink-0 bg-[#F47221]"
                    aria-hidden="true"
                  />
                  <div>
                    <h3 className="text-base font-semibold text-[#143A66]">
                      {p.title}
                    </h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#143A66]/70">
                      {p.text}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative" data-testid="academics-courses-section">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 pb-24 md:pb-32 lg:grid-cols-[1.15fr_0.85fr] lg:gap-20">
          <div className="order-2 lg:order-1">
            <Reveal>
              <p className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#F47221]">
                <span className="font-display text-2xl tracking-normal text-[#143A66]/25">
                  02
                </span>
                Course Studies
              </p>
              <h2
                className="mt-5 font-display text-3xl font-semibold text-[#143A66] md:text-4xl"
                data-testid="courses-heading"
              >
                Course Studies by Class
              </h2>
            </Reveal>
            <div
              className="mt-10 border-t border-[#143A66]/10"
              data-testid="course-table"
            >
              {COURSES.map((row, i) => (
                <Reveal
                  key={row.classes}
                  delay={0.05 * i}
                  className="grid gap-3 border-b border-[#143A66]/10 py-6 sm:grid-cols-[190px_1fr] sm:gap-6"
                >
                  <div data-testid={`course-row-${i}`}>
                    <p className="font-display text-lg font-semibold text-[#143A66]">
                      {row.classes}
                    </p>
                    {row.note && (
                      <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#F47221]">
                        {row.note}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap content-start gap-2">
                    {row.subjects.map((s) => (
                      <span
                        key={s}
                        className="rounded-full border border-[#143A66]/15 bg-white/70 px-3 py-1 text-xs font-medium text-[#143A66]"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-10">
              <button
                type="button"
                onClick={goContact}
                data-testid="academics-contact-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#143A66] transition-colors hover:text-[#F47221]"
              >
                Have questions?
                <span className="underline decoration-[#F47221] decoration-2 underline-offset-8">
                  Contact us
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>
          <Reveal className="order-1 self-start lg:order-2 lg:sticky lg:top-28">
            <FramedImage
              src="/academics-coursestudies.jpg"
              alt="Course studies at Val-Hue"
              testId="coursestudies-image"
            />
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
